import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  MenuItem,
  Select,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Grid,
  TableContainer,
  Snackbar
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../services/api';
import { useRefresh } from '../contexts/RefreshContext';
import * as XLSX from 'xlsx';

const InvoicePage = () => {
  const [hotels, setHotels] = useState([]);
  const [hotelId, setHotelId] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [invoiceData, setInvoiceData] = useState([]);
  const { refreshKey } = useRefresh();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    const fetchHotels = async () => {
      const res = await api.get('/hotels');
      setHotels(res.data);
    };
    fetchHotels();
  }, []);

  useEffect(() => {
    if (hotelId && dateFrom && dateTo) {
      fetchInvoice();
    }
  }, [refreshKey]);
  
  const fetchInvoice = async () => {
    if (!hotelId || !dateFrom || !dateTo) return;

    const res = await api.get('/invoice', {
      params: {
        hotel_id: hotelId,
        date_from: dateFrom.toISOString().split('T')[0],
        date_to: dateTo.toISOString().split('T')[0]
      }
    });
    setInvoiceData(res.data);
  };

  const calculateTotal = (key) => {
    return invoiceData.reduce((acc, item) => acc + (item[key] || 0), 0);
  };
  
  const showFeedback = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleColumnCopy = (columnKey, columnName) => {
    if (invoiceData.length === 0) {
      showFeedback('Não há dados para copiar.');
      return;
    }
    
    // Se a coluna for 'date', o tratamento é diferente (não numérico)
    if (columnKey === 'date') {
      const dateValues = invoiceData.map(row => row.date);
      // Adiciona a palavra "Total" para alinhar com as outras colunas ao colar
      const clipboardText = [...dateValues, 'Total'].join('\n');
      navigator.clipboard.writeText(clipboardText).then(() => {
        showFeedback(`Coluna "${columnName}" copiada com sucesso!`);
      });
      return;
    }

    // Lógica para colunas numéricas
    const columnValues = invoiceData.map(row => row[columnKey].toFixed(2));
    const totalValue = calculateTotal(columnKey).toFixed(2);
    const clipboardText = [...columnValues, totalValue].join('\n');

    navigator.clipboard.writeText(clipboardText).then(() => {
      showFeedback(`Coluna "${columnName}" copiada com sucesso!`);
    });
  };

  const handleExportToExcel = useCallback(() => {
    if (invoiceData.length === 0) {
      showFeedback('Não há dados para exportar. Faça uma busca primeiro.');
      return;
    }

    const headers = ['Date', 'Cash Sales', 'Room Charge', 'Credit Card', 'Square', 'Totals', 'Gratuity'];
    
    const dataForExport = invoiceData.map(row => ({
        Date: row.date,
        'Cash Sales': row.cash_sales,
        'Room Charge': row.room_charge,
        'Credit Card': row.credit_card,
        Square: row.square,
        Totals: row.total,
        Gratuity: row.gratuity,
    }));

    const totalRow = {
        Date: 'Total',
        'Cash Sales': calculateTotal('cash_sales'),
        'Room Charge': calculateTotal('room_charge'),
        'Credit Card': calculateTotal('credit_card'),
        Square: calculateTotal('square'),
        Totals: calculateTotal('total'),
        Gratuity: calculateTotal('gratuity'),
    };
    
    const worksheet = XLSX.utils.json_to_sheet(dataForExport, { header: headers });
    XLSX.utils.sheet_add_json(worksheet, [totalRow], { skipHeader: true, origin: -1 });
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoice');
    
    const hotelName = hotels.find(h => h.id === hotelId)?.name.replace(/\s/g, '_') || 'Export';
    const dateString = `${dateFrom.toISOString().split('T')[0]}_to_${dateTo.toISOString().split('T')[0]}`;
    XLSX.writeFile(workbook, `Invoice_${hotelName}_${dateString}.xlsx`);
    
    showFeedback('Exportação para Excel iniciada.');

  }, [invoiceData, hotelId, dateFrom, dateTo, hotels]);

  useEffect(() => {
    window.addEventListener('exportInvoiceToExcel', handleExportToExcel);
    return () => {
        window.removeEventListener('exportInvoiceToExcel', handleExportToExcel);
    };
  }, [handleExportToExcel]);


  return (
    <Box sx={{
      mt: -4,
      pt: -4,
      p: 1,
      maxWidth: '1000px',
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      gap: 2
    }}>
      <Paper
        elevation={0}
        sx={{
          pt: 2,
          px: 2,
          pb: 2,
          borderRadius: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={1}
            direction="row"
            sx={{
              justifyContent: "center",
              alignItems: "center"}}
          >
            <Grid item xs={12} sm={3}>
              <Select
                value={hotelId}
                onChange={(e) => setHotelId(e.target.value)}
                displayEmpty
                fullWidth
                sx={{ bgcolor: 'white', borderRadius: 1, minWidth: '200px' }}
              >
                <MenuItem value="">Select Resort</MenuItem>
                {hotels.map((hotel) => (
                  <MenuItem key={hotel.id} value={hotel.id}>{hotel.name}</MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={3}>
              <DatePicker
                label="Start Date"
                value={dateFrom}
                onChange={(newValue) => setDateFrom(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <DatePicker
                label="End Date"
                value={dateTo}
                onChange={(newValue) => setDateTo(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
                minDate={dateFrom}
              />
            </Grid>

            <Grid item xs={12} sm={3} container spacing={1}>
                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={fetchInvoice}
                        disabled={!hotelId || !dateFrom || !dateTo}
                        sx={{ fontWeight: 'bold', height: '56px' }}
                    >
                        Search
                    </Button>
                </Grid>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Paper>

      {invoiceData.length > 0 && (
        <Paper
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            backgroundColor: '#fff'
          }}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell 
                    sx={{ minWidth: 100, cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' } }}
                    onClick={() => handleColumnCopy('date', 'Date')}
                  ><strong>Date</strong></TableCell>
                  
                  <TableCell 
                    align="right" 
                    sx={{ minWidth: 80, cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' } }}
                    onClick={() => handleColumnCopy('cash_sales', 'Cash Sales')}
                  ><strong>Cash Sales</strong></TableCell>

                  <TableCell 
                    align="right" 
                    sx={{ minWidth: 80, cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' } }}
                    onClick={() => handleColumnCopy('room_charge', 'Room Charge')}
                  ><strong>Room Charge</strong></TableCell>
                  
                  <TableCell 
                    align="right" 
                    sx={{ minWidth: 80, cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' } }}
                    onClick={() => handleColumnCopy('credit_card', 'Credit Card')}
                  ><strong>Credit Card</strong></TableCell>

                  <TableCell 
                    align="right" 
                    sx={{ minWidth: 80, cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' } }}
                    onClick={() => handleColumnCopy('square', 'Square')}
                  ><strong>Square</strong></TableCell>

                  <TableCell 
                    align="right" 
                    sx={{ minWidth: 80, cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' } }}
                    onClick={() => handleColumnCopy('total', 'Totals')}
                  ><strong>Totals</strong></TableCell>

                  <TableCell 
                    align="right" 
                    sx={{ minWidth: 80, cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' } }}
                    onClick={() => handleColumnCopy('gratuity', 'Gratuity')}
                  ><strong>Gratuity</strong></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {invoiceData.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.date}</TableCell>
                    <TableCell align="right">${row.cash_sales.toFixed(2)}</TableCell>
                    <TableCell align="right">${row.room_charge.toFixed(2)}</TableCell>
                    <TableCell align="right">${row.credit_card.toFixed(2)}</TableCell>
                    <TableCell align="right">${row.square.toFixed(2)}</TableCell>
                    <TableCell align="right">${row.total.toFixed(2)}</TableCell>
                    <TableCell align="right">${row.gratuity.toFixed(2)}</TableCell>
                  </TableRow>
                ))}

                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Total</strong></TableCell>
                  <TableCell align="right"><strong>${calculateTotal('cash_sales').toFixed(2)}</strong></TableCell>
                  <TableCell align="right"><strong>${calculateTotal('room_charge').toFixed(2)}</strong></TableCell>
                  <TableCell align="right"><strong>${calculateTotal('credit_card').toFixed(2)}</strong></TableCell>
                  <TableCell align="right"><strong>${calculateTotal('square').toFixed(2)}</strong></TableCell>
                  <TableCell align="right"><strong>${calculateTotal('total').toFixed(2)}</strong></TableCell>
                  <TableCell align="right"><strong>${calculateTotal('gratuity').toFixed(2)}</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
       <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default InvoicePage;